"use client";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MoreHorizontal,
} from "lucide-react";

export interface PaginationInfo {
  page: number;
  pageSize: number;   
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

type PageToken = number | "dots";

function buildPages(
  current: number,
  total: number,
  windowSize: number,
  clampEdges: boolean
): PageToken[] {
  if (total <= 1) return [1];
  if (total <= windowSize + (clampEdges ? 2 : 0)) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const half = Math.floor(windowSize / 2);
  let start = Math.max(1, current - half);
  let end = Math.min(total, start + windowSize - 1);
  if (end - start + 1 < windowSize) start = Math.max(1, end - windowSize + 1);

  const out: PageToken[] = [];
  if (clampEdges && start > 1) {
    out.push(1);
    if (start > 2) out.push("dots");
  }
  for (let p = start; p <= end; p++) out.push(p);
  if (clampEdges && end < total) {
    if (end < total - 1) out.push("dots");
    out.push(total);
  }
  return out;
}

interface PaginationProps {
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;

  showFirstLast?: boolean;
  showPageNumbers?: boolean;
  showPageSize?: boolean;
  showInfo?: boolean;
  pageSizeOptions?: number[];
  maxPageButtons?: number;
  clampEdges?: boolean;
  className?: string;

  showPageOf?: boolean;   
  compact?: boolean;      
  sticky?: boolean;       
  loading?: boolean;      
}

export function AppPagination({
  pagination,
  onPageChange,
  onPageSizeChange,
  showFirstLast = true,
  showPageNumbers = true,
  showPageSize = true,
  showInfo = true,
  pageSizeOptions = [10, 15, 20, 50],
  maxPageButtons = 5,
  clampEdges = true,
  className = "",
  showPageOf = true,
  compact = false,
  sticky = false,
  loading = false,
}: PaginationProps) {
  const { page: currentPage, totalPages, total, pageSize } = pagination;

  if (!total || totalPages <= 0) return null;

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, total);

  const pageTokens = showPageNumbers
    ? buildPages(currentPage, totalPages, Math.max(3, maxPageButtons), clampEdges)
    : [];

  const disabled = loading;

  const go = (p: number) => {
    if (p < 1 || p > totalPages || p === currentPage) return;
    onPageChange(p);
  };
  React.useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (disabled) return;
      if (e.key === "ArrowLeft") go(currentPage - 1);
      if (e.key === "ArrowRight") go(currentPage + 1);
      if (e.key.toLowerCase() === "home") go(1);
      if (e.key.toLowerCase() === "end") go(totalPages);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [currentPage, totalPages, disabled]);

  const [ariaMsg, setAriaMsg] = React.useState("");
  React.useEffect(() => {
    setAriaMsg(`Page ${currentPage} of ${totalPages}`);
  }, [currentPage, totalPages]);

  const wrapperBase =
    "w-full " +
    (sticky
      ? "sticky bottom-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/75 border-t border-border/50"
      : "");

  const innerBase = compact ? "py-2 px-3" : "py-4 px-4";

  return (
    <nav
      className={`${wrapperBase} ${className}`}
      aria-label="Pagination Navigation"
      role="navigation"
    >
      <div className="sr-only" aria-live="polite">
        {ariaMsg}
      </div>

      <div className={`mx-auto flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${innerBase}`}>
        <div className="flex items-center gap-3 text-sm">
          {showInfo && (
            <div className="text-muted-foreground">
              Showing{" "}
              <span className="font-medium text-foreground">{startItem}</span> to{" "}
              <span className="font-medium text-foreground">{endItem}</span> of{" "}
              <span className="font-medium text-foreground">{total}</span> entries
            </div>
          )}
          {showPageOf && (
            <div className="hidden md:inline-flex items-center px-2.5 py-1 rounded-full text-xs border bg-muted/40 text-muted-foreground">
              Page <span className="mx-1 font-medium text-foreground">{currentPage}</span>
              of <span className="ml-1 font-medium text-foreground">{totalPages}</span>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          {/* Page size */}
          {showPageSize && onPageSizeChange && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Rows per page</span>
              <Select
                key={`page-size-${pageSize}`}
                value={String(pageSize)}
                onValueChange={(v) => onPageSizeChange(parseInt(v))}
                disabled={disabled}
              >
                <SelectTrigger className={compact ? "h-8 w-[86px]" : "w-[86px]"}>
                  <SelectValue placeholder={String(pageSize)} />
                </SelectTrigger>
                <SelectContent>
                  {pageSizeOptions.map((s) => (
                    <SelectItem key={s} value={String(s)}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Buttons */}
          <div className="flex items-center gap-1">
            {showFirstLast && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                className={`rounded-full ${compact ? "h-8 w-8" : "h-9 w-9"}`}
                onClick={() => go(1)}
                disabled={currentPage === 1 || disabled}
                aria-label="First page"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
            )}

            <Button
              type="button"
              variant="outline"
              size="icon"
              className={`rounded-full ${compact ? "h-8 w-8" : "h-9 w-9"}`}
              onClick={() => go(currentPage - 1)}
              disabled={currentPage === 1 || disabled}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            {showPageNumbers &&
              pageTokens.map((tok, i) =>
                tok === "dots" ? (
                  <div
                    key={`dots-${i}`}
                    className={`inline-flex items-center justify-center text-muted-foreground ${compact ? "h-8 w-8" : "h-9 w-9"}`}
                    aria-hidden
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </div>
                ) : (
                  <Button
                    key={tok}
                    type="button"
                    variant={tok === currentPage ? "default" : "outline"}
                    size="icon"
                    className={`rounded-full transition-all ${compact ? "h-8 w-8" : "h-9 w-9"} ${
                      tok === currentPage ? "shadow-sm" : "hover:bg-accent hover:text-accent-foreground"
                    }`}
                    aria-current={tok === currentPage ? "page" : undefined}
                    onClick={() => go(tok)}
                    disabled={disabled}
                  >
                    {tok}
                  </Button>
                )
              )}

            <Button
              type="button"
              variant="outline"
              size="icon"
              className={`rounded-full ${compact ? "h-8 w-8" : "h-9 w-9"}`}
              onClick={() => go(currentPage + 1)}
              disabled={currentPage === totalPages || disabled}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>

            {showFirstLast && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                className={`rounded-full ${compact ? "h-8 w-8" : "h-9 w-9"}`}
                onClick={() => go(totalPages)}
                disabled={currentPage === totalPages || disabled}
                aria-label="Last page"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
